/** @module @liquidefy/code-editor/filesystem/filesystem-tree @version 1.0.0 @license LQD Research LLC */

// ═══ §1 IMPORTS ═══

import type {
  FileEntry,
  DirectoryEntry,
  FileId,
  DirectoryId,
  EditorResult,
} from '../code-editor-types.js';

import {
  ok,
  err,
  generateId,
  asFileId,
  asDirectoryId,
  now,
} from '../code-editor.js';

// ═══ §2 TREE BUILDING — construct and mutate file trees from flat lists ═══

/**
 * Build a DirectoryEntry tree from a flat list of FileEntry items.
 * Groups files by directory segments under the given rootPath.
 */
export function buildFileTree(entries: FileEntry[], rootPath: string = '/'): EditorResult<DirectoryEntry> {
  const normalizedRoot = normalizePath(rootPath);
  const root: DirectoryEntry = emptyDirectory(
    asDirectoryId(generateId('dir')),
    normalizedRoot,
    extractName(normalizedRoot)
  );

  let tree = root;
  for (const entry of entries) {
    const result = addToTree(tree, entry);
    if (!result.success) {
      return err('TREE_BUILD_FAILED', `Failed to add ${entry.path}: ${result.message}`);
    }
    tree = result.value;
  }

  return ok(tree, 'TREE_BUILT', `Built tree with ${entries.length} entries`);
}

/**
 * Add a FileEntry or DirectoryEntry into the tree at its correct position.
 * Creates intermediate directories as needed.
 */
export function addToTree(tree: DirectoryEntry, entry: FileEntry | DirectoryEntry): EditorResult<DirectoryEntry> {
  const entryDir = parentPath(entry.path);
  const treePath = normalizePath(tree.path);

  if (normalizePath(entryDir) === treePath) {
    const existing = tree.children.find(c => c.path === entry.path);
    if (existing) {
      return err('DUPLICATE_ENTRY', `Entry already exists at ${entry.path}`);
    }
    return ok(
      { ...tree, children: [...tree.children, entry] },
      'ENTRY_ADDED',
      `Added ${entry.name} to ${tree.path}`
    );
  }

  const segments = relativeParts(treePath, entryDir);
  if (segments.length === 0) {
    return err('INVALID_PATH', `Cannot place ${entry.path} under ${tree.path}`);
  }

  return ok(
    ensureAndInsert(tree, segments, entry),
    'ENTRY_ADDED',
    `Added ${entry.name} under ${tree.path}`
  );
}

/**
 * Remove a node from the tree by its full path.
 */
export function removeFromTree(tree: DirectoryEntry, path: string): EditorResult<DirectoryEntry> {
  const normalized = normalizePath(path);
  const result = removeNode(tree, normalized);
  if (!result.found) {
    return err('NOT_FOUND', `Path not found: ${path}`);
  }
  return ok(result.tree, 'ENTRY_REMOVED', `Removed ${path}`);
}

/**
 * Move a node from oldPath to newPath within the tree.
 */
export function moveInTree(tree: DirectoryEntry, oldPath: string, newPath: string): EditorResult<DirectoryEntry> {
  const node = getNode(tree, oldPath);
  if (!node) {
    return err('NOT_FOUND', `Source path not found: ${oldPath}`);
  }

  const removeResult = removeFromTree(tree, oldPath);
  if (!removeResult.success) return removeResult;

  const newName = extractName(newPath);
  const moved = rewritePaths(node, oldPath, newPath);
  const renamedNode = isDirectoryNode(moved)
    ? { ...moved, name: newName, path: normalizePath(newPath) }
    : { ...moved, name: newName, path: normalizePath(newPath) } as FileEntry;

  return addToTree(removeResult.value, renamedNode);
}

/**
 * Rename a node at the given path to newName, keeping it in the same directory.
 */
export function renameInTree(tree: DirectoryEntry, path: string, newName: string): EditorResult<DirectoryEntry> {
  const dir = parentPath(path);
  const newPath = dir === '/' ? `/${newName}` : `${dir}/${newName}`;
  return moveInTree(tree, path, newPath);
}

/**
 * Merge updates into an existing tree. Entries in updates that exist in existing
 * are replaced; new entries are added; entries only in existing are kept.
 */
export function mergeTree(existing: DirectoryEntry, updates: DirectoryEntry): EditorResult<DirectoryEntry> {
  const merged = mergeDirectories(existing, updates);
  return ok(merged, 'TREE_MERGED', 'Trees merged successfully');
}

/**
 * Factory for creating an empty DirectoryEntry.
 */
export function emptyDirectory(id: DirectoryId, path: string, name: string): DirectoryEntry {
  return {
    id,
    path: normalizePath(path),
    name,
    children: [],
    isExpanded: false,
  };
}

// --- internal helpers for tree building ---

function normalizePath(p: string): string {
  if (!p || p === '/') return '/';
  let result = p.replace(/\\/g, '/');
  if (result.length > 1 && result.endsWith('/')) {
    result = result.slice(0, -1);
  }
  return result;
}

function extractName(p: string): string {
  const normalized = normalizePath(p);
  if (normalized === '/') return '/';
  const parts = normalized.split('/');
  return parts[parts.length - 1] || '/';
}

function parentPath(p: string): string {
  const normalized = normalizePath(p);
  if (normalized === '/') return '/';
  const idx = normalized.lastIndexOf('/');
  if (idx <= 0) return '/';
  return normalized.slice(0, idx);
}

function relativeParts(base: string, target: string): string[] {
  const b = normalizePath(base);
  const t = normalizePath(target);
  if (t === b) return [];
  if (!t.startsWith(b === '/' ? '/' : b + '/')) return [];
  const rel = b === '/' ? t.slice(1) : t.slice(b.length + 1);
  return rel.split('/').filter(s => s.length > 0);
}

function isDirectoryNode(node: FileEntry | DirectoryEntry): node is DirectoryEntry {
  return 'children' in node;
}

function ensureAndInsert(tree: DirectoryEntry, segments: string[], entry: FileEntry | DirectoryEntry): DirectoryEntry {
  if (segments.length === 0) {
    return { ...tree, children: [...tree.children, entry] };
  }

  const [head, ...rest] = segments;
  const childPath = tree.path === '/' ? `/${head}` : `${tree.path}/${head}`;

  const existingIdx = tree.children.findIndex(
    c => isDirectoryNode(c) && c.name === head
  );

  if (existingIdx >= 0) {
    const existingDir = tree.children[existingIdx] as DirectoryEntry;
    const updatedDir = ensureAndInsert(existingDir, rest, entry);
    const newChildren = [...tree.children];
    newChildren[existingIdx] = updatedDir;
    return { ...tree, children: newChildren };
  }

  const newDir = emptyDirectory(
    asDirectoryId(generateId('dir')),
    childPath,
    head!
  );
  const populatedDir = ensureAndInsert(newDir, rest, entry);
  return { ...tree, children: [...tree.children, populatedDir] };
}

function removeNode(tree: DirectoryEntry, path: string): { tree: DirectoryEntry; found: boolean } {
  const idx = tree.children.findIndex(c => normalizePath(c.path) === path);
  if (idx >= 0) {
    const newChildren = [...tree.children];
    newChildren.splice(idx, 1);
    return { tree: { ...tree, children: newChildren }, found: true };
  }

  let found = false;
  const newChildren = tree.children.map(child => {
    if (!isDirectoryNode(child)) return child;
    const result = removeNode(child, path);
    if (result.found) found = true;
    return result.found ? result.tree : child;
  });

  return { tree: { ...tree, children: newChildren }, found };
}

function rewritePaths(node: FileEntry | DirectoryEntry, oldBase: string, newBase: string): FileEntry | DirectoryEntry {
  const oldNorm = normalizePath(oldBase);
  const newNorm = normalizePath(newBase);

  if (!isDirectoryNode(node)) {
    const newPath = node.path.replace(oldNorm, newNorm);
    return { ...node, path: newPath, name: extractName(newPath) };
  }

  const newPath = node.path.replace(oldNorm, newNorm);
  return {
    ...node,
    path: newPath,
    name: extractName(newPath),
    children: node.children.map(c => rewritePaths(c, oldBase, newBase)),
  };
}

function mergeDirectories(existing: DirectoryEntry, updates: DirectoryEntry): DirectoryEntry {
  const childMap = new Map<string, FileEntry | DirectoryEntry>();

  for (const child of existing.children) {
    childMap.set(child.path, child);
  }

  for (const child of updates.children) {
    const existingChild = childMap.get(child.path);
    if (existingChild && isDirectoryNode(existingChild) && isDirectoryNode(child)) {
      childMap.set(child.path, mergeDirectories(existingChild, child));
    } else {
      childMap.set(child.path, child);
    }
  }

  return {
    ...existing,
    children: Array.from(childMap.values()),
  };
}


// ═══ §3 TREE TRAVERSAL — depth-first, breadth-first, find, path queries ═══

type TreeVisitor = (node: FileEntry | DirectoryEntry, depth: number) => boolean | void;

/**
 * Depth-first walk of the tree. Return false from visitor to stop traversal.
 */
export function walkTree(tree: DirectoryEntry, visitor: TreeVisitor): void {
  walkDepthFirst(tree, visitor, 0);
}

function walkDepthFirst(node: FileEntry | DirectoryEntry, visitor: TreeVisitor, depth: number): boolean {
  const result = visitor(node, depth);
  if (result === false) return false;

  if (isDirectoryNode(node)) {
    for (const child of node.children) {
      const cont = walkDepthFirst(child, visitor, depth + 1);
      if (!cont) return false;
    }
  }
  return true;
}

/**
 * Breadth-first walk of the tree.
 */
export function walkBreadthFirst(tree: DirectoryEntry, visitor: TreeVisitor): void {
  const queue: Array<{ node: FileEntry | DirectoryEntry; depth: number }> = [
    { node: tree, depth: 0 },
  ];

  while (queue.length > 0) {
    const item = queue.shift()!;
    const result = visitor(item.node, item.depth);
    if (result === false) return;

    if (isDirectoryNode(item.node)) {
      for (const child of item.node.children) {
        queue.push({ node: child, depth: item.depth + 1 });
      }
    }
  }
}

/**
 * Find the first node matching the predicate (depth-first).
 */
export function findInTree(
  tree: DirectoryEntry,
  predicate: (node: FileEntry | DirectoryEntry) => boolean
): FileEntry | DirectoryEntry | undefined {
  let found: FileEntry | DirectoryEntry | undefined;
  walkTree(tree, (node) => {
    if (predicate(node)) {
      found = node;
      return false;
    }
    return;
  });
  return found;
}

/**
 * Find all nodes matching the predicate.
 */
export function findAllInTree(
  tree: DirectoryEntry,
  predicate: (node: FileEntry | DirectoryEntry) => boolean
): (FileEntry | DirectoryEntry)[] {
  const results: (FileEntry | DirectoryEntry)[] = [];
  walkTree(tree, (node) => {
    if (predicate(node)) results.push(node);
  });
  return results;
}

/**
 * Return the chain of ancestor DirectoryEntry nodes from root to the node at path.
 */
export function pathToNode(tree: DirectoryEntry, path: string): DirectoryEntry[] {
  const normalized = normalizePath(path);
  const ancestors: DirectoryEntry[] = [];

  function search(node: DirectoryEntry): boolean {
    ancestors.push(node);
    if (normalizePath(node.path) === normalized) return true;

    for (const child of node.children) {
      if (isDirectoryNode(child)) {
        if (search(child)) return true;
      } else if (normalizePath(child.path) === normalized) {
        return true;
      }
    }

    ancestors.pop();
    return false;
  }

  search(tree);
  return ancestors;
}

/**
 * Get a node by its full path.
 */
export function getNode(tree: DirectoryEntry, path: string): FileEntry | DirectoryEntry | undefined {
  const normalized = normalizePath(path);
  if (normalizePath(tree.path) === normalized) return tree;

  return findInTree(tree, node => normalizePath(node.path) === normalized);
}

/**
 * Get the parent DirectoryEntry for a node at the given path.
 */
export function getParent(tree: DirectoryEntry, path: string): DirectoryEntry | undefined {
  const ancestors = pathToNode(tree, path);
  if (ancestors.length < 2) return undefined;
  return ancestors[ancestors.length - 2];
}

/**
 * Get siblings of the node at path (same parent, excluding self).
 */
export function getSiblings(tree: DirectoryEntry, path: string): (FileEntry | DirectoryEntry)[] {
  const parent = getParent(tree, path);
  if (!parent) return [];
  const normalized = normalizePath(path);
  return parent.children.filter(c => normalizePath(c.path) !== normalized);
}

/**
 * Get children of the node at path. Returns empty array for files.
 */
export function getChildren(tree: DirectoryEntry, path: string): (FileEntry | DirectoryEntry)[] {
  const node = getNode(tree, path);
  if (!node || !isDirectoryNode(node)) return [];
  return node.children;
}

/**
 * Compute the maximum depth of the tree.
 */
export function treeDepth(tree: DirectoryEntry): number {
  let maxDepth = 0;
  walkTree(tree, (_node, depth) => {
    if (depth > maxDepth) maxDepth = depth;
  });
  return maxDepth;
}

/**
 * Count all nodes (files + directories) in the tree.
 */
export function nodeCount(tree: DirectoryEntry): number {
  let count = 0;
  walkTree(tree, () => { count++; });
  return count;
}


// ═══ §4 TREE FILTERING — deep filter, by extension, pattern, gitignore-style ═══

/**
 * Default hidden patterns (gitignore-style) for file trees.
 */
export const DEFAULT_HIDDEN_PATTERNS: readonly string[] = [
  '.git',
  'node_modules',
  '.DS_Store',
  'dist',
  '.next',
  '.nuxt',
  '.svelte-kit',
  '.cache',
  '.parcel-cache',
  '.turbo',
  'coverage',
  '__pycache__',
  '.pytest_cache',
  '.mypy_cache',
  'target',
  'build',
  '.gradle',
  '.idea',
  '.vscode',
  '.env',
  '.env.local',
  'thumbs.db',
  '*.swp',
  '*.swo',
  '*~',
];

/**
 * Deep filter that preserves directory structure. Directories are kept
 * if they contain at least one matching descendant.
 */
export function filterTree(
  tree: DirectoryEntry,
  predicate: (node: FileEntry | DirectoryEntry) => boolean
): DirectoryEntry {
  const filteredChildren: (FileEntry | DirectoryEntry)[] = [];

  for (const child of tree.children) {
    if (isDirectoryNode(child)) {
      const filtered = filterTree(child, predicate);
      if (filtered.children.length > 0 || predicate(child)) {
        filteredChildren.push(filtered);
      }
    } else {
      if (predicate(child)) {
        filteredChildren.push(child);
      }
    }
  }

  return { ...tree, children: filteredChildren };
}

/**
 * Filter tree to only include files with the given extensions.
 */
export function filterByExtension(tree: DirectoryEntry, extensions: string[]): DirectoryEntry {
  const normalized = extensions.map(e => e.startsWith('.') ? e : `.${e}`);
  return filterTree(tree, node => {
    if (isDirectoryNode(node)) return false;
    return normalized.some(ext => (node as FileEntry).extension === ext);
  });
}

/**
 * Filter tree to files whose names match the given glob pattern.
 */
export function filterByPattern(tree: DirectoryEntry, pattern: string): DirectoryEntry {
  return filterTree(tree, node => {
    if (isDirectoryNode(node)) return false;
    return matchesGlob(node.name, pattern);
  });
}

/**
 * Exclude entries matching any of the given patterns.
 */
export function excludePatterns(tree: DirectoryEntry, patterns: string[]): DirectoryEntry {
  return filterTree(tree, node => {
    return !patterns.some(p => isHidden(node.path, [p]) || isHidden(node.name, [p]));
  });
}

/**
 * Return a tree with hidden files/directories removed. Uses DEFAULT_HIDDEN_PATTERNS
 * if no patterns provided.
 */
export function visibleTree(tree: DirectoryEntry, hiddenPatterns?: string[]): DirectoryEntry {
  const patterns = hiddenPatterns ?? DEFAULT_HIDDEN_PATTERNS as string[];
  return excludePatterns(tree, patterns);
}


// ═══ §5 TREE SORTING — recursive sort with various comparators ═══

/**
 * Sort priority: directories always come before files.
 */
export const SORT_PRIORITY = { directory: 0, file: 1 } as const;

type SortComparator = (a: FileEntry | DirectoryEntry, b: FileEntry | DirectoryEntry) => number;

/**
 * Recursively sort the tree using the given comparator.
 */
export function sortTree(tree: DirectoryEntry, comparator: SortComparator): DirectoryEntry {
  const sortedChildren = [...tree.children]
    .sort(comparator)
    .map(child => {
      if (isDirectoryNode(child)) return sortTree(child, comparator);
      return child;
    });

  return { ...tree, children: sortedChildren };
}

/**
 * Sort by name, directories first.
 */
export function sortByName(tree: DirectoryEntry, caseSensitive: boolean = false): DirectoryEntry {
  return sortTree(tree, (a, b) => {
    const aIsDir = isDirectoryNode(a) ? 0 : 1;
    const bIsDir = isDirectoryNode(b) ? 0 : 1;
    if (aIsDir !== bIsDir) return aIsDir - bIsDir;

    const aName = caseSensitive ? a.name : a.name.toLowerCase();
    const bName = caseSensitive ? b.name : b.name.toLowerCase();
    return aName < bName ? -1 : aName > bName ? 1 : 0;
  });
}

/**
 * Sort by modification time, most recent first. Directories first.
 */
export function sortByModified(tree: DirectoryEntry): DirectoryEntry {
  return sortTree(tree, (a, b) => {
    const aIsDir = isDirectoryNode(a) ? 0 : 1;
    const bIsDir = isDirectoryNode(b) ? 0 : 1;
    if (aIsDir !== bIsDir) return aIsDir - bIsDir;

    const aTime = isDirectoryNode(a) ? 0 : (a as FileEntry).modifiedAt;
    const bTime = isDirectoryNode(b) ? 0 : (b as FileEntry).modifiedAt;
    return bTime - aTime;
  });
}

/**
 * Sort by file size, largest first. Directories first.
 */
export function sortBySize(tree: DirectoryEntry): DirectoryEntry {
  return sortTree(tree, (a, b) => {
    const aIsDir = isDirectoryNode(a) ? 0 : 1;
    const bIsDir = isDirectoryNode(b) ? 0 : 1;
    if (aIsDir !== bIsDir) return aIsDir - bIsDir;

    const aSize = isDirectoryNode(a) ? 0 : (a as FileEntry).size;
    const bSize = isDirectoryNode(b) ? 0 : (b as FileEntry).size;
    return bSize - aSize;
  });
}

/**
 * Sort by type: directories first, then group by extension, then alphabetically.
 */
export function sortByType(tree: DirectoryEntry): DirectoryEntry {
  return sortTree(tree, (a, b) => {
    const aIsDir = isDirectoryNode(a) ? 0 : 1;
    const bIsDir = isDirectoryNode(b) ? 0 : 1;
    if (aIsDir !== bIsDir) return aIsDir - bIsDir;

    if (!isDirectoryNode(a) && !isDirectoryNode(b)) {
      const aExt = (a as FileEntry).extension.toLowerCase();
      const bExt = (b as FileEntry).extension.toLowerCase();
      if (aExt !== bExt) return aExt < bExt ? -1 : 1;
    }

    const aName = a.name.toLowerCase();
    const bName = b.name.toLowerCase();
    return aName < bName ? -1 : aName > bName ? 1 : 0;
  });
}

/**
 * Sort by file extension alphabetically, directories first.
 */
export function sortByExtension(tree: DirectoryEntry): DirectoryEntry {
  return sortTree(tree, (a, b) => {
    const aIsDir = isDirectoryNode(a) ? 0 : 1;
    const bIsDir = isDirectoryNode(b) ? 0 : 1;
    if (aIsDir !== bIsDir) return aIsDir - bIsDir;

    const aExt = isDirectoryNode(a) ? '' : (a as FileEntry).extension.toLowerCase();
    const bExt = isDirectoryNode(b) ? '' : (b as FileEntry).extension.toLowerCase();
    if (aExt !== bExt) return aExt < bExt ? -1 : 1;

    return a.name.toLowerCase() < b.name.toLowerCase() ? -1 : 1;
  });
}


// ═══ §6 TREE DISPLAY — ASCII representation, serialization, stats, diff ═══

interface TreeToStringOptions {
  readonly indent?: string;
  readonly showSize?: boolean;
  readonly showModified?: boolean;
  readonly maxDepth?: number;
}

/**
 * Render the tree as an ASCII tree string.
 */
export function treeToString(tree: DirectoryEntry, options?: TreeToStringOptions): string {
  const lines: string[] = [];
  const indent = options?.indent ?? '  ';
  const maxDepth = options?.maxDepth ?? Infinity;

  function render(node: FileEntry | DirectoryEntry, prefix: string, isLast: boolean, depth: number): void {
    if (depth > maxDepth) return;

    const connector = depth === 0 ? '' : isLast ? '└── ' : '├── ';
    let label = node.name;

    if (isDirectoryNode(node)) {
      label += '/';
    } else if (options?.showSize) {
      label += ` (${formatSize((node as FileEntry).size)})`;
    }

    if (!isDirectoryNode(node) && options?.showModified) {
      label += ` [${new Date((node as FileEntry).modifiedAt).toISOString().slice(0, 10)}]`;
    }

    lines.push(`${prefix}${connector}${label}`);

    if (isDirectoryNode(node)) {
      const children = node.children;
      for (let i = 0; i < children.length; i++) {
        const childPrefix = depth === 0
          ? ''
          : prefix + (isLast ? `${indent}` : `│${indent.slice(1)}`);
        render(children[i]!, childPrefix, i === children.length - 1, depth + 1);
      }
    }
  }

  render(tree, '', true, 0);
  return lines.join('\n');
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes}B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)}KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)}GB`;
}

/**
 * Convert tree to a plain JSON-serializable structure.
 */
export function treeToJSON(tree: DirectoryEntry): Record<string, unknown> {
  return {
    id: tree.id,
    path: tree.path,
    name: tree.name,
    isExpanded: tree.isExpanded,
    children: tree.children.map(child => {
      if (isDirectoryNode(child)) return treeToJSON(child);
      return { ...child };
    }),
  };
}

/**
 * Flatten a tree into a flat list of FileEntry items (no directories).
 */
export function flattenTree(tree: DirectoryEntry): FileEntry[] {
  const files: FileEntry[] = [];
  walkTree(tree, (node) => {
    if (!isDirectoryNode(node)) {
      files.push(node as FileEntry);
    }
  });
  return files;
}

interface TreeStats {
  readonly files: number;
  readonly directories: number;
  readonly totalSize: number;
  readonly deepestPath: string;
}

/**
 * Compute aggregate statistics for the tree.
 */
export function treeStats(tree: DirectoryEntry): TreeStats {
  let files = 0;
  let directories = 0;
  let totalSize = 0;
  let deepestPath = tree.path;
  let maxDepth = 0;

  walkTree(tree, (node, depth) => {
    if (isDirectoryNode(node)) {
      directories++;
    } else {
      files++;
      totalSize += (node as FileEntry).size;
    }
    if (depth > maxDepth) {
      maxDepth = depth;
      deepestPath = node.path;
    }
  });

  return { files, directories, totalSize, deepestPath };
}

interface TreeDiffResult {
  readonly added: string[];
  readonly removed: string[];
  readonly modified: string[];
  readonly renamed: Array<{ from: string; to: string }>;
}

/**
 * Compute the diff between two trees.
 * Detects added, removed, and modified entries. Renames are detected
 * when a file with the same size and similar name appears removed in
 * one location and added in another.
 */
export function treeDiff(oldTree: DirectoryEntry, newTree: DirectoryEntry): TreeDiffResult {
  const oldFiles = new Map<string, FileEntry>();
  const newFiles = new Map<string, FileEntry>();

  walkTree(oldTree, (node) => {
    if (!isDirectoryNode(node)) {
      oldFiles.set(node.path, node as FileEntry);
    }
  });

  walkTree(newTree, (node) => {
    if (!isDirectoryNode(node)) {
      newFiles.set(node.path, node as FileEntry);
    }
  });

  const added: string[] = [];
  const removed: string[] = [];
  const modified: string[] = [];
  const renamed: Array<{ from: string; to: string }> = [];

  const removedCandidates: FileEntry[] = [];
  const addedCandidates: FileEntry[] = [];

  for (const [path, entry] of oldFiles) {
    const newEntry = newFiles.get(path);
    if (!newEntry) {
      removedCandidates.push(entry);
    } else if (newEntry.modifiedAt !== entry.modifiedAt || newEntry.size !== entry.size) {
      modified.push(path);
    }
  }

  for (const [path, entry] of newFiles) {
    if (!oldFiles.has(path)) {
      addedCandidates.push(entry);
    }
  }

  const matchedRenames = new Set<string>();

  for (const rem of removedCandidates) {
    const match = addedCandidates.find(
      add => !matchedRenames.has(add.path) && add.size === rem.size && add.name === rem.name
    );
    if (match) {
      renamed.push({ from: rem.path, to: match.path });
      matchedRenames.add(match.path);
    } else {
      removed.push(rem.path);
    }
  }

  for (const add of addedCandidates) {
    if (!matchedRenames.has(add.path)) {
      added.push(add.path);
    }
  }

  return { added, removed, modified, renamed };
}


// ═══ §7 PATH UTILITIES — path tests, glob matching, common ancestor ═══

/**
 * Check if child path is anywhere under parent path.
 */
export function isChildOf(parent: string, child: string): boolean {
  const p = normalizePath(parent);
  const c = normalizePath(child);
  if (p === c) return false;
  return p === '/' ? true : c.startsWith(p + '/');
}

/**
 * Check if child path is a direct child (not nested deeper) of parent.
 */
export function isDirectChildOf(parent: string, child: string): boolean {
  const p = normalizePath(parent);
  const c = normalizePath(child);
  if (!isChildOf(p, c)) return false;
  const relative = p === '/' ? c.slice(1) : c.slice(p.length + 1);
  return !relative.includes('/');
}

/**
 * Find the deepest common ancestor of multiple paths.
 */
export function commonAncestor(paths: string[]): string {
  if (paths.length === 0) return '/';
  if (paths.length === 1) return parentPath(paths[0]!);

  const splitPaths = paths.map(p => normalizePath(p).split('/').filter(Boolean));
  const minLen = Math.min(...splitPaths.map(p => p.length));

  const common: string[] = [];
  for (let i = 0; i < minLen; i++) {
    const segment = splitPaths[0]![i]!;
    if (splitPaths.every(p => p[i] === segment)) {
      common.push(segment);
    } else {
      break;
    }
  }

  return common.length === 0 ? '/' : '/' + common.join('/');
}

/**
 * Compute the relative path from one path to another.
 */
export function relativePath(from: string, to: string): string {
  const fromParts = normalizePath(from).split('/').filter(Boolean);
  const toParts = normalizePath(to).split('/').filter(Boolean);

  let commonLen = 0;
  const minLen = Math.min(fromParts.length, toParts.length);
  for (let i = 0; i < minLen; i++) {
    if (fromParts[i] === toParts[i]) {
      commonLen++;
    } else {
      break;
    }
  }

  const ups = fromParts.length - commonLen;
  const downs = toParts.slice(commonLen);

  const parts: string[] = [];
  for (let i = 0; i < ups; i++) parts.push('..');
  parts.push(...downs);

  return parts.length === 0 ? '.' : parts.join('/');
}

/**
 * Check if a path or name is hidden according to the given patterns.
 */
export function isHidden(path: string, patterns: string[]): boolean {
  const name = extractName(path);
  return patterns.some(p => matchesGlob(name, p) || matchesGlob(path, p));
}

/**
 * Simple glob matching supporting * (any chars except /) and ** (any including /).
 * Also supports ? for single char.
 */
export function matchesGlob(path: string, pattern: string): boolean {
  if (pattern === path) return true;

  const regexStr = globToRegex(pattern);
  try {
    const re = new RegExp(`^${regexStr}$`);
    return re.test(path);
  } catch {
    return path === pattern;
  }
}

/**
 * Expand a glob pattern into a regex string. Supports *, **, and ?.
 */
export function expandGlob(pattern: string): string {
  return globToRegex(pattern);
}

function globToRegex(pattern: string): string {
  let result = '';
  let i = 0;
  while (i < pattern.length) {
    const ch = pattern[i]!;
    if (ch === '*') {
      if (i + 1 < pattern.length && pattern[i + 1] === '*') {
        if (i + 2 < pattern.length && pattern[i + 2] === '/') {
          result += '(?:.+/)?';
          i += 3;
        } else {
          result += '.*';
          i += 2;
        }
      } else {
        result += '[^/]*';
        i++;
      }
    } else if (ch === '?') {
      result += '[^/]';
      i++;
    } else if ('.+^${}()|[]\\'.includes(ch)) {
      result += '\\' + ch;
      i++;
    } else {
      result += ch;
      i++;
    }
  }
  return result;
}
